import { fbm, ridged, mulberry32, seedAt, weightedPick, randRange } from '../core/Rng';
import { pickBiome, biomes, type BiomeDef } from '../data/biomes';
import { veinMaterials } from '../data/materials';
import { resourceDefs, oreResourceId } from '../data/resources';
import type { DifficultyDef } from '../data/difficulty';

/**
 * Procedural dünya üretimi.
 * Tamamen seed'e bağlı ve saf (yan etkisiz): aynı seed + aynı koordinat
 * her zaman aynı sonucu verir. Bu yüzden chunk'lar bellekten atılıp geri
 * yüklendiğinde dünya birebir aynı gelir ve kaydedilmesi gerekmez —
 * yalnızca oyuncunun YAPTIĞI değişiklikler kaydedilir.
 */

export const CHUNK_SIZE = 32;
export const WATER_LEVEL = 0;

export interface WorldParams {
  seed: number;
  /** Hikâye modu: el yapımı, sınırlı harita. Normal: sınırsız procedural. */
  mode: 'story' | 'normal';
  difficulty: DifficultyDef;
  /** Hikâye modu harita yarıçapı (birim). */
  storyRadius?: number;
}

const HEIGHT_SCALE = 26;

/** Arazi yüksekliği (birim). Su seviyesi 0. */
export function sampleHeight(p: WorldParams, x: number, z: number): number {
  const s = p.seed;
  // Kıta şekli: geniş ölçekli alçak frekans
  const continent = fbm(s, x * 0.0016, z * 0.0016, { octaves: 4, gain: 0.5 });
  // Tepe/dağ detayı
  const hills = fbm(s + 101, x * 0.006, z * 0.006, { octaves: 4, gain: 0.55 });
  const detail = fbm(s + 202, x * 0.03, z * 0.03, { octaves: 2, gain: 0.5 });

  // KARA/DENİZ DENGESİ: fbm ortalaması 0 civarında olduğu için ham toplam
  // dünyanın yarısından fazlasını su seviyesinin altında bırakıyordu (ölçüldü: %75 su).
  // Pozitif taban kaydırması karayı baskın hale getirir; deniz yine geniş kalır.
  const LAND_BIAS = 0.34;
  let h = continent * 1.15 + hills * 0.55 + detail * 0.12 + LAND_BIAS;

  // Dağ maskesi: yalnızca yüksek bölgelerde sırtları güçlendir
  const mountainMask = Math.max(0, continent - 0.12) * 2.4;
  if (mountainMask > 0) {
    h += ridged(s + 303, x * 0.004, z * 0.004, { octaves: 4 }) * mountainMask * 1.5;
  }

  h *= HEIGHT_SCALE;

  // Nehir oyma: ridged noise'un tepe çizgisi nehir yatağı olur
  const river = riverStrength(p, x, z);
  if (river > 0) {
    h -= river * 16;
  }

  // Hikâye modunda harita kenarında zemin denize iner (sınırlı ada hissi)
  if (p.mode === 'story') {
    const r = Math.hypot(x, z);
    const limit = p.storyRadius ?? 640;
    if (r > limit * 0.78) {
      const t = Math.min(1, (r - limit * 0.78) / (limit * 0.22));
      h -= t * t * 70;
    }
  }
  return h;
}

/** 0..1: nehir yatağının etkisi. */
export function riverStrength(p: WorldParams, x: number, z: number): number {
  const n = ridged(p.seed + 777, x * 0.0011, z * 0.0011, { octaves: 2 });
  // 0.86 üstü değerler nehir çizgisi
  const t = (n - 0.86) / 0.14;
  if (t <= 0) return 0;
  return Math.min(1, t);
}

/** Büyük nehir: hikâye modunda ve normal modda haritayı boydan boya geçer. */
export function isMajorRiver(p: WorldParams, x: number, z: number): boolean {
  return riverStrength(p, x, z) > 0.72;
}

export interface BiomeSample {
  biome: BiomeDef;
  temp: number;
  moist: number;
  height: number;
  /** Su altında mı? */
  underwater: boolean;
  /** Kıyı şeridi mi? */
  shore: boolean;
}

export function sampleBiome(p: WorldParams, x: number, z: number): BiomeSample {
  const height = sampleHeight(p, x, z);
  // Sıcaklık kuzey-güney gradyanı + gürültü, yükseklikle düşer
  const latitude = Math.max(0, Math.min(1, 0.5 + z / 6000));
  let temp = 0.5 + fbm(p.seed + 411, x * 0.0009, z * 0.0009, { octaves: 3 }) * 0.55 - (latitude - 0.5) * 0.5;
  temp -= Math.max(0, height / HEIGHT_SCALE) * 0.16;
  let moist = 0.5 + fbm(p.seed + 512, x * 0.0012, z * 0.0012, { octaves: 3 }) * 0.6;
  temp = Math.max(0, Math.min(1, temp));
  moist = Math.max(0, Math.min(1, moist));

  const underwater = height < WATER_LEVEL;
  const shore = height >= WATER_LEVEL && height < 1.6;

  // Nadir biyom (mor zehirli) için ayrı, çok seyrek maske
  const rareRoll = (fbm(p.seed + 909, x * 0.0007, z * 0.0007, { octaves: 2 }) + 1) / 2;

  let biome: BiomeDef;
  if (shore || (underwater && height > -6)) {
    biome = biomes.require('beach');
  } else {
    biome = pickBiome(temp, moist, rareRoll);
    // Yükseklik dağ biyomunu zorlar
    if (height > HEIGHT_SCALE * 1.35) biome = biomes.require('mountain');
  }
  return { biome, temp, moist, height, underwater, shore };
}

// ------------------------------------------------------------------ kaynak yerleşimi

export interface ResourceInstance {
  /** Kalıcı kimlik: kayıtta bu id ile "toplandı" işaretlenir. */
  id: string;
  def: string;
  x: number;
  y: number;
  z: number;
  rotY: number;
  scale: number;
}

/**
 * Bir chunk'ın kaynaklarını üretir.
 * Yerleşim yalnızca seed + chunk koordinatına bağlıdır; oyuncunun topladığı
 * kaynaklar ChunkManager tarafından filtrelenir.
 */
export function generateChunkResources(p: WorldParams, cx: number, cz: number): ResourceInstance[] {
  const out: ResourceInstance[] = [];
  const rand = mulberry32(seedAt(p.seed, cx, cz, 1));
  const originX = cx * CHUNK_SIZE;
  const originZ = cz * CHUNK_SIZE;

  // Hikâye modu sınırının dışına kaynak koyma
  if (p.mode === 'story') {
    const limit = p.storyRadius ?? 640;
    if (Math.hypot(originX, originZ) > limit + CHUNK_SIZE * 2) return out;
  }

  // Chunk merkezinden biyom örneklemesi (kaynak yoğunluğu için yeterli)
  const center = sampleBiome(p, originX + CHUNK_SIZE / 2, originZ + CHUNK_SIZE / 2);
  const b = center.biome;
  const yieldMul = p.difficulty.resourceYield;
  const oreMul = p.difficulty.oreFrequency;

  const area = (CHUNK_SIZE * CHUNK_SIZE) / 100; // 100 birim² başına yoğunluk
  const treeCount = Math.round(b.trees * area * 2.2 * Math.min(1.5, yieldMul));
  const rockCount = Math.round(b.rocks * area * 1.4 * Math.min(1.5, yieldMul));
  const plantCount = Math.round(b.grass * area * 1.1 * Math.min(1.5, yieldMul));

  let index = 0;
  const place = (defId: string, count: number, opts: { minHeight?: number; maxHeight?: number; scaleVar?: number } = {}) => {
    const def = resourceDefs.get(defId);
    if (!def) return;
    for (let i = 0; i < count; i++) {
      const x = originX + rand() * CHUNK_SIZE;
      const z = originZ + rand() * CHUNK_SIZE;
      const h = sampleHeight(p, x, z);
      const minH = opts.minHeight ?? 0.8;
      const maxH = opts.maxHeight ?? Infinity;
      index++;
      if (h < minH || h > maxH) continue;
      // Nehir yatağına bitki/ağaç koyma
      if (riverStrength(p, x, z) > 0.25) continue;
      out.push({
        id: `${cx}_${cz}_${index}`,
        def: defId,
        x,
        y: h,
        z,
        rotY: rand() * Math.PI * 2,
        scale: 1 + (rand() - 0.5) * (opts.scaleVar ?? 0.35),
      });
    }
  };

  // Biyoma göre ağaç türü
  const treeType =
    b.id === 'venom' ? 'tree_venom' : b.id === 'ice' ? 'tree_frost' : b.id === 'forest' || b.id === 'mountain' ? 'tree_pine' : 'tree_oak';
  place(treeType, treeCount, { minHeight: 1.2, scaleVar: 0.5 });
  place('rock_boulder', rockCount, { minHeight: 0.6 });

  if (b.id === 'beach') {
    place('sand_pile', Math.round(area * 1.6), { minHeight: 0.1, maxHeight: 3.2 });
  }
  if (b.id === 'venom') {
    place('plant_glow', Math.round(plantCount * 0.8), { minHeight: 0.8 });
  } else {
    place('bush_berry', Math.round(plantCount * 0.45), { minHeight: 1 });
    place('plant_fiber', Math.round(plantCount * 0.9), { minHeight: 0.9 });
  }

  // --- Maden damarları: yüzeyde görünen çıkıntılar ---
  // (Derin madencilik mağara sisteminde; yüzey damarları erken oyun için.)
  const veins = veinMaterials();
  const pool = veins.map((m) => {
    const bias = b.oreBias?.[m.id] ?? 1;
    return { weight: m.vein!.frequency * bias, value: m } as const;
  });
  const veinTries = Math.round(2.5 * oreMul);
  for (let i = 0; i < veinTries; i++) {
    const m = weightedPick(rand, pool);
    if (!m) continue;
    const x = originX + rand() * CHUNK_SIZE;
    const z = originZ + rand() * CHUNK_SIZE;
    const h = sampleHeight(p, x, z);
    index++;
    if (h < 1.5) continue;
    // Yüzey damarı: madenin derinlik aralığı yüksek arazide daha olası
    const depthScore = Math.min(1, Math.max(0, (h - m.vein!.minDepth * 0.25) / 40));
    if (rand() > depthScore * 0.7 + 0.08) continue;
    if (m.id === 'venomcrystal' && b.id !== 'venom') continue;
    const cluster = Math.round(randRange(rand, m.vein!.clusterMin, m.vein!.clusterMax) * 0.4);
    for (let c = 0; c <= cluster; c++) {
      const ox = x + (rand() - 0.5) * 4;
      const oz = z + (rand() - 0.5) * 4;
      const oh = sampleHeight(p, ox, oz);
      if (oh < 1.2) continue;
      out.push({
        id: `${cx}_${cz}_o${index}_${c}`,
        def: oreResourceId(m.id),
        x: ox,
        y: oh,
        z: oz,
        rotY: rand() * Math.PI * 2,
        scale: 0.85 + rand() * 0.4,
      });
    }
  }

  return out;
}

/** Chunk'ta canlı doğma noktası önerir (AI sistemi kullanır). */
export function pickSpawn(p: WorldParams, cx: number, cz: number, night: boolean): { creature: string; x: number; y: number; z: number } | null {
  const rand = mulberry32(seedAt(p.seed, cx, cz, night ? 33 : 22));
  const x = cx * CHUNK_SIZE + rand() * CHUNK_SIZE;
  const z = cz * CHUNK_SIZE + rand() * CHUNK_SIZE;
  const s = sampleBiome(p, x, z);
  if (s.underwater) return null;
  const pool = s.biome.spawns
    .filter((e) => (night ? true : !e.night))
    .map((e) => ({ weight: e.weight * (e.night && night ? p.difficulty.nightSpawn : 1), value: e.creature }));
  const creature = weightedPick(rand, pool);
  if (!creature) return null;
  return { creature, x, y: s.height, z };
}
